import { z } from 'zod'
import { styleSourceSchema } from './template-article'

/**
 * The pull's wire contract (CLI-994 moved it here from report-issue.ts): what the editor's Pull
 * answers, the day-by-day follow-up it may ask for next, and what a header keeps of its last
 * pull's failures. Sources are the issue's source families (`issueSourceSchema`, the template's
 * `styleSourceSchema` by another name).
 */

/**
 * One source a header's last pull could not fetch (CLI-994), as the issue stores it: what the
 * editor says on the header, and whether asking again day by day may still bring it.
 */
export const sectionPullFailureSchema = z.object({
  source: styleSourceSchema,
  /** What went wrong, as the pull reported it. Never a stack or a query. */
  reason: z.string(),
  retryByDay: z.boolean(),
})
export type SectionPullFailure = z.infer<typeof sectionPullFailureSchema>

export const pullIssueResponseSchema = z.object({
  added: z.number().int().nonnegative(),
  /** Already in the issue, in any state — a re-pull never resurrects what was removed. */
  skipped: z.number().int().nonnegative(),
  sections: z.array(
    z.object({ sectionId: z.uuid(), name: z.string(), added: z.number().int().nonnegative() }),
  ),
  /**
   * Headers whose content could not be fetched, one entry per header and source. A pull asks the
   * data API once per header per source, and one of those calls failing used to throw away the
   * whole issue — thirty headers that answered fine included. They are reported instead, so the
   * issue keeps what it got and the editor can say which headers to pull again.
   */
  failed: z.array(
    z.object({
      sectionId: z.uuid(),
      name: z.string(),
      source: styleSourceSchema,
      /** What went wrong, for the editor to show. Never a stack or a query. */
      reason: z.string(),
      /**
       * The request covered more than a day and timed out, or the data API failed it (CLI-994):
       * asking again one day at a time may answer where the whole window could not. The editor
       * does exactly that, in a request of its own (`pullIssueQuerySchema`); the system's builds
       * do it inline.
       */
      retryByDay: z.boolean(),
    }),
  ),
  /**
   * The rows this pull inserted (CLI-982) — what the editor marks "Just pulled", and nothing
   * else: not the previous pull's rows, not an article added by hand. Named by the server because
   * only the writer knows which rows it wrote; the old rule, "created after the last pull", read a
   * timestamp taken before the inserts against rows stamped by the database after them, and so
   * marked every previous pull's articles again. Empty when nothing was written (a dry build).
   */
  addedItemIds: z.array(z.uuid()),
})
export type PullIssueResponse = z.infer<typeof pullIssueResponseSchema>

/**
 * Pull one header again, day by day (CLI-994): the editor's follow-up for a header whose request
 * over the whole window failed. Only that header is pulled, each day of the window in a request of
 * its own, side by side — so the follow-up answers inside the public hostname's proxy timeout,
 * which the failed request and its pieces together would not.
 */
export const pullIssueQuerySchema = z.object({ byDay: z.uuid().optional() })
